import {FC, useEffect, useState} from 'react';

import {
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  SelectChangeEvent,
  Checkbox,
  ListItemText,
  FormControl,
  FormControlProps,
  useTheme,
  Typography,
  InputLabelProps,
  SxProps,
  Theme,
} from '@mui/material';

import {getFieldStyle} from '../../layouts/theme/FieldStyles';

interface Option {
  id: string;
  label: string;
}
interface EmpionMultiSelectProps {
  label: string;
  options: Option[];
  onChange: (event: any) => void;
  filteringFor?: string;
  backgroundVariant?: 'light' | 'dark';
  displayDefaultOptions?: any[];
  maxSelections?: number;
  currentValue?: string[];
  formControlStyles?: SxProps<Theme>;
  isClearFilters?: boolean;
}

export const EmpionMultiSelect: FC<
  EmpionMultiSelectProps & Omit<SelectProps, 'variant'> & FormControlProps & InputLabelProps
> = ({
  label,
  options,
  onChange,
  filteringFor,
  backgroundVariant,
  displayDefaultOptions,
  maxSelections,
  currentValue,
  formControlStyles,
  isClearFilters,
  ...props
}) => {
  const [displayOptions, setDisplayOptions] = useState<string[]>([]);

  const theme = useTheme();

  useEffect(() => {
    if (currentValue) {
      setDisplayOptions(currentValue);
    }
  }, [currentValue]);

  // Clear filters
  useEffect(() => {
    if (isClearFilters) {
      setDisplayOptions([]);
    }
  }, [isClearFilters]);

  // Better to set initial state in useEffect, if it comes in as props.
  useEffect(() => {
    if (displayDefaultOptions) {
      setDisplayOptions(displayDefaultOptions);
    }
  }, [displayDefaultOptions]);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const {
      target: {value},
    } = event;

    let lookupValues: string[] = typeof value === 'string' ? value.split(',') : (value as string[]);

    if (maxSelections && lookupValues.length > maxSelections) {
      lookupValues = lookupValues.slice(0, maxSelections);
    }

    const findIds = lookupValues?.map((value) => {
      return options.find((option) => option.label === value)?.id;
    });

    if (filteringFor) {
      const returnObjectsOfItemsToBeFiltered = filteringFor && {
        [filteringFor as string]: findIds,
      };

      onChange(returnObjectsOfItemsToBeFiltered as any);
    } else {
      onChange(findIds);
    }

    setDisplayOptions(lookupValues);
  };

  // Material UI MultiSelect default styles
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      },
    },
  };

  return (
    <div>
      <FormControl
        sx={{
          ...getFieldStyle(theme, backgroundVariant),
          ...formControlStyles,
          width: '100%',
        }}
      >
        <InputLabel
          sx={{
            marginTop: '-8px',
            '&.MuiInputLabel-shrink': {
              marginTop: '0px',
            },
          }}
          id='empion-multiple-checkbox-label'
        >
          <Typography>{label.charAt(0).toUpperCase() + label.slice(1)}</Typography>
        </InputLabel>
        <Select
          size='medium'
          labelId='empion-multiple-checkbox-label'
          id='empion-multiple-checkbox'
          data-testid='empion-multiple-checkbox'
          {...props}
          label={label}
          multiple
          value={displayOptions}
          onChange={handleChange}
          renderValue={(selected: unknown) => (selected as string[]).join(', ')}
          MenuProps={MenuProps}
        >
          {options?.map((option) => (
            <MenuItem
              key={option.id}
              value={option.label}
              sx={{}}
              disabled={
                !!(maxSelections && displayOptions.length >= maxSelections && !displayOptions.includes(option.label))
              }
            >
              <Checkbox
                sx={{
                  color: theme.palette.text.primary,
                  '&.Mui-checked': {
                    color: theme.palette.text.primary,
                  },
                }}
                checked={displayOptions.indexOf(option.label) > -1}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
